import "rxjs/add/observable/throw";
import "rxjs/add/operator/timeoutWith";
import "rxjs/add/operator/toPromise";

import { Injectable } from "@angular/core";
import { Observable } from "rxjs/Observable";
import * as toastr from "toastr";

import { environment } from "../../environments/environment";
import { AdjustmentCode } from "../models/adjCode.Model";
import { AuditHistory } from "../models/auditHistory.model";
import { ServiceEventAdvancedSearchCriteria } from "../models/serviceEventAdvancedSearchCriteria.model";
import { ServiceEventReassign } from "../models/serviceEventReassign.model";
import { BaseService } from "./api-base.service";
import { ReconApiService } from "./recon-api.service";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";

export enum EventSearchType {
  Claims = "Claims",
  Remittances = "Cash",
}
@Injectable()
export class ReconApiSearchService {
  private readonly baseHeaders: HttpHeaders;
  private readonly baseService: BaseService;
  private readonly Timeout: number = 30000;
  private readonly limit: number = 9999;
  private readonly offset: number = 0;

  constructor(
    private http: HttpClient,
    private reconApiService: ReconApiService
  ) {
    this.baseService = new BaseService();
    this.baseHeaders = this.baseService.getHeaders();
  }
  public async getServiceEvent<T>(
    type: EventSearchType,
    serviceEventId: string,
    clientId: string,
    sort?: string,
    posted?: boolean,
    isAdvancedSearch?: boolean,
    advancedSearchCriteria?: ServiceEventAdvancedSearchCriteria
  ): Promise<T[]> {
    // Build query params
    let params = new HttpParams()
      .set("limit", this.limit.toString())
      .set("offset", this.offset.toString())
      .set("posted", posted ? "true" : "false");

    if (sort) {
      params = params.set("sort", sort);
    }

    const baseUrl = `${environment.reconApiRoot.url}/client/${clientId}/ServiceEvent/${serviceEventId}/${type}/`;

    if (isAdvancedSearch && advancedSearchCriteria) {
      const body = Object.assign({}, advancedSearchCriteria, {
        isAdvancedSearch: `${isAdvancedSearch}`,
      });

      const fullUrl = `${baseUrl}?posted=${
        posted ? "true" : "false"
      }&${params.toString()}`;

      const result = await this.http
        .post<{ Items: T[] }>(
          `${baseUrl}?posted=${posted ? "true" : "false"}`,
          body,
          {
            headers: this.baseHeaders,
            params: params,
          }
        )
        .timeoutWith(
          this.Timeout,
          Observable.throw(new Error("Timeout exceeded."))
        )
        .toPromise()
        .catch((error) => {
          console.log(
            `Error on ${type.toString()} occurred at ${new Date()} status: ${
              error && error.status ? error.status.toString() : "unknown"
            }`
          );
          this.reconApiService.logToElasticSearchFromUI(error, fullUrl);
          return [];
        });

      // error case: return empty array
      if (!result) {
        return [];
      }

      return result.Items || [];
    }

    const simpleUrl = baseUrl;

    const response = await this.http
      .get<{ Items: T[] }>(simpleUrl, {
        headers: this.baseHeaders,
        params: params,
      })
      .timeoutWith(
        this.Timeout,
        Observable.throw(new Error("Timeout exceeded."))
      )
      .toPromise()
      .catch((error) => {
        console.log(
          `Error on ${type.toString()} occurred at ${new Date()} status: ${
            error && error.status ? error.status.toString() : "unknown"
          }`
        );
        this.reconApiService.logToElasticSearchFromUI(error, simpleUrl);
        return [];
      });

    if (!response) {
      return [];
    }

    return response.Items || [];
  }

  public async CashAndClaimsAssignment<T>(
    serviceEventId: string,
    clientId: string,
    listOfReassigns: ServiceEventReassign[]
  ): Promise<boolean> {
    const url = `${environment.reconApiRoot.url}/client/${clientId}/ServiceEvent/${serviceEventId}/CashAndClaimsAssignment/`;
    const response = await this.http
      .put<boolean>(url, listOfReassigns, { headers: this.baseHeaders })
      .timeoutWith(
        this.Timeout,
        Observable.throw(new Error("Timeout exceeded."))
      )
      .toPromise()
      .catch((error) => {
        console.log(
          `Error on Cash and Claims Reassignment occurred at ${new Date()} status: ${
            error.status
          }`
        );
        this.reconApiService.logToElasticSearchFromUI(error, url);
        toastr.error(`Unable to assign Remittance or Claim to Service Event.`);
        return false;
      });
    return response;
  }

  public async getAuditHistory(
    clientId: string,
    referenceNumber: number,
    type: string
  ): Promise<AuditHistory[]> {
    const url = `${environment.reconApiRoot.url}/client/${clientId}/ServiceEvent/AuditHistory/?Type=${type}&ReferenceNumber=${referenceNumber}`;
    const response = await this.http
      .get<AuditHistory[]>(url, { headers: this.baseHeaders })
      .timeoutWith(
        this.Timeout,
        Observable.throw(new Error("Timeout exceeded."))
      )
      .toPromise()
      .catch((error) => {
        console.log(
          `Error occurred at ${new Date()} status: ${error.status.toString()}`
        );
        this.reconApiService.logToElasticSearchFromUI(error, url);
        return [];
      });
    if (!response) {
      return [];
    }
    return response.Items || [];
  }

  public async getAdjCodeForRemittance(
    clientId: string,
    paymentBatch: string,
    sequence: number,
    paymentReference: string,
    posted?: boolean
  ): Promise<AdjustmentCode[]> {
    const baseUrl =
      `${environment.reconApiRoot.url}/client/${clientId}/Adjustment/PaymentBatch/${paymentBatch}` +
      `/Sequence/${sequence}/PaymentReference/${paymentReference}`;

    let params = new HttpParams()
      .set("limit", this.limit.toString())
      .set("posted", posted ? "true" : "false");

    const response = await this.http
      .get<AdjustmentCode[]>(baseUrl, {
        headers: this.baseHeaders,
        params: params,
      })
      .timeoutWith(
        this.Timeout,
        Observable.throw(new Error("Timeout exceeded."))
      )
      .toPromise()
      .catch((error) => {
        console.log(
          `Error occurred at ${new Date()} status: ${error.status.toString()}`
        );
        this.reconApiService.logToElasticSearchFromUI(error, baseUrl);
        return [];
      });

    if (!response) {
      return [];
    }

    return response.Items || [];
  }

  public async getTransactionHistory(
    serviceEventId: string,
    clientId: string
  ): Promise<any[]> {
    let params = new HttpParams()
      .set("limit", this.limit.toString())
      .set("offset", this.offset.toString());

    const url = `${environment.reconApiRoot.url}/client/${clientId}/ServiceEvent/${serviceEventId}/TransactionHistory/`;

    const response = await this.http
      .get<{ Items: any[] }>(url, {
        headers: this.baseHeaders,
        params: params,
      })
      .timeoutWith(
        this.Timeout,
        Observable.throw(new Error("Timeout exceeded."))
      )
      .toPromise()
      .catch((error) => {
        console.log(
          `Error occurred at ${new Date()} status: ${
            error && error.status ? error.status.toString() : "unknown"
          }`
        );
        const fullUrl = `${url}?${params.toString()}`;
        this.reconApiService.logToElasticSearchFromUI(error, fullUrl);

        return [];
      });

    if (!response) {
      return [];
    }

    return response.Items || [];
  }
}



// WEBPACK FOOTER //
// ./src/app/services/recon-api.search.service.ts